import { Expert, Invitation } from "@/types/expert-programs-types";
import { RequestRow } from "@/types/dashboard-types";
import { useUserStore } from "@/stores/user";
import { ref } from "vue";

export function useExpertsList() {
  const userStore = useUserStore();

  const sortedRows = ref({
    requests: [] as RequestRow[],
    activeExperts: [] as Expert[],
    interestedExperts: [] as Expert[],
    interestedSignupExperts: [] as Expert[],
    invitedExperts: [] as Expert[],
    invitations: [] as Invitation[],
    hiddenExperts: [] as Expert[],
    otherExperts: [] as Expert[],
  });

  const convertToDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const determineInvitedUserSignupStartedDate = (expertRow: Expert) => {
    if (expertRow.programInvitation) {
      if (expertRow.programInvitation.updatedAt > expertRow.createdAt) {
        return expertRow.programInvitation.updatedAt;
      } else {
        return expertRow.createdAt;
      }
    } else {
      return (
        expertRow.programsAddedAt[userStore.selectedProgram?.id || ""] ||
        expertRow.createdAt
      );
    }
  };

  const determineInvitedSignupInProgressLastActivityText = (
    expertRow: Expert
  ) => {
    if (
      expertRow.lastCompleteSignupReminderSentAt &&
      expertRow.lastCompleteSignupReminderSentAt > expertRow.createdAt
    ) {
      return (
        "Reminder sent " +
        convertToDate(expertRow.lastCompleteSignupReminderSentAt)
      );
    } else {
      return (
        "Started signup " +
        convertToDate(determineInvitedUserSignupStartedDate(expertRow))
      );
    }
  };

  const determineCalendarDisconnectDate = (expertRow: Expert) => {
    if (
      expertRow.lastSyncCalendarReminderSentAt &&
      expertRow.lastSyncCalendarReminderSentAt >
        expertRow.calendarProviderAccount?.accountExpiredAt
    ) {
      return (
        "Reminder sent at " +
        convertToDate(expertRow.lastSyncCalendarReminderSentAt)
      );
    } else if (
      expertRow.lastSyncCalendarReminderSentAt &&
      expertRow.calendarProviderAccount?.accountExpiredAt === null
    ) {
      return (
        "Reminder sent at " +
        convertToDate(expertRow.lastSyncCalendarReminderSentAt)
      );
    } else {
      return (
        "Hidden since " +
        convertToDate(
          expertRow.calendarProviderAccount?.accountExpiredAt ||
            expertRow.updatedAt
        )
      );
    }
  };

  const pendingInvitation = (
    expert: Expert,
    originalInvitationRows: Invitation[]
  ) => {
    return originalInvitationRows.some(
      (invitation) =>
        invitation.userEmail === expert.email && invitation.status === "pending"
    );
  };

  const status = (status: string) => {
    if (status === "approved") {
      return "Active";
    } else if (status === "rejected") {
      return "Rejected";
    } else if (status === "pending") {
      return "Pending";
    } else if (status === "archived") {
      return "Inactive";
    } else {
      return "Interested";
    }
  };

  const expertStatusText = (
    joinRequestStatus: string,
    expert: Expert,
    originalInvitationRows: Invitation[]
  ) => {
    if (expert.onboardingStep !== "completed") {
      return "Signup In Progress";
    } else {
      if (joinRequestStatus === "") {
        if (expert.joinedProgramViaInvite) {
          return "Invited";
        } else {
          if (pendingInvitation(expert, originalInvitationRows)) {
            return "Invited";
          } else {
            return "Interested in Applying";
          }
        }
      } else if (joinRequestStatus === "approved") {
        return expert.active ? "Active" : "Hidden - Calendar Disconnected";
      } else {
        return status(joinRequestStatus);
      }
    }
  };

  const lastActivityText = (
    expertRow: Expert,
    originalInvitationRows: Invitation[]
  ) => {
    const expertStatus = expertStatusText(
      expertRow.joinProgramRequest?.[0]?.status || "",
      expertRow,
      originalInvitationRows
    );

    switch (expertStatus) {
      case "Active":
        return (
          "Active since " +
          convertToDate(expertRow.joinProgramRequest?.[0]?.updatedAt)
        );

      case "Inactive":
        return (
          "Inactive since " +
          convertToDate(expertRow.joinProgramRequest?.[0]?.updatedAt)
        );

      case "Hidden - Calendar Disconnected":
        return determineCalendarDisconnectDate(expertRow);

      case "Interested in Applying":
        return (
          "Interested since " +
          convertToDate(
            expertRow.programsAddedAt[userStore.selectedProgram?.id || ""] ||
              expertRow.createdAt
          )
        );

      case "Invited":
        if (expertRow.programInvitation?.lastReminderSentAt) {
          return (
            "Reminder sent " +
            convertToDate(expertRow.programInvitation.lastReminderSentAt)
          );
        } else {
          return (
            "Invited on " +
            convertToDate(
              expertRow.programInvitation?.createdAt || expertRow.updatedAt
            )
          );
        }

      case "Signup In Progress":
        return determineInvitedSignupInProgressLastActivityText(expertRow);

      case "Pending":
        return (
          "Applied on " +
          convertToDate(
            expertRow.joinProgramRequest?.[0]?.updatedAt || expertRow.updatedAt
          )
        );

      default:
        return "";
    }
  };

  const determineActionType = (
    expertRow: Expert,
    originalInvitationRows: Invitation[]
  ) => {
    const userStatus = expertStatusText(
      expertRow.joinProgramRequest?.[0]?.status || "",
      expertRow,
      originalInvitationRows
    );

    if (userStatus === "Hidden - Calendar Disconnected") {
      return "send_calendar_disconnected_reminder";
    } else if (userStatus === "Signup In Progress") {
      return "send_complete_signup_reminder";
    } else if (userStatus === "Interested in Applying") {
      return "send_invitation_email";
    } else if (userStatus === "Invited") {
      return "send_invitation_email";
    } else {
      return "handle_suspend_expert";
    }
  };

  const performActionText = (
    joinRequestStatus: string,
    expert: Expert,
    originalInvitationRows: Invitation[]
  ) => {
    const status = expertStatusText(
      joinRequestStatus || "",
      expert,
      originalInvitationRows
    );

    if (status === "Interested in Applying") {
      return "Send Invitation";
    } else if (status === "Invited") {
      return "Send Reminder to Complete Signup";
    } else if (status === "Signup In Progress") {
      return "Send Reminder to Complete Signup";
    } else if (status === "Hidden - Calendar Disconnected") {
      return "Send reminder to sync calendar";
    } else if (joinRequestStatus === "archived") {
      return "Make active";
    } else if (joinRequestStatus === "approved" && expert.active) {
      return "Make inactive";
    }

    return "";
  };

  const getClassForStatus = (
    expert: Expert,
    originalInvitationRows: Invitation[]
  ) => {
    const status = expertStatusText(
      expert.joinProgramRequest?.[0]?.status || "",
      expert,
      originalInvitationRows
    );

    switch (status) {
      case "Active":
        return "text-green-800 bg-green-100";
      case "Inactive":
        return "text-red-800 bg-red-100";
      case "Hidden - Calendar Disconnected":
        return "text-blue-800 bg-blue-100";
      case "Rejected":
        return "text-red-800 bg-red-100";
      case "Interested in Applying":
        return "text-purple-800 bg-purple-100";
      case "Signup In Progress":
        return "text-yellow-800 bg-yellow-100";
      default:
        return "text-gray-600 bg-gray-200";
    }
  };

  const getStatusTooltip = (status: string) => {
    switch (status) {
      case "Active":
        return "Completed setup and active in your program.";
      case "Inactive":
        return "Expert is a member of your program but has been deactivated and is not visible to your customers.";
      case "Interested in Applying":
        return "Searched and selected your software during signup but hasn't received an invitation yet.";
      case "Invited":
        return "Has been invited to apply but hasn't yet submitted their profile.";
      case "Signup In Progress":
        return "Has accepted the invitation and is currently completing the signup process.";
      case "Hidden - Calendar Disconnected":
        return "Was active in your program but their calendar was disconnected so they are hidden from your program until reconnected.";
      case "Pending":
        return "Expert's application is pending review";
      default:
        return "Expert status";
    }
  };

  const expertButtonText = (status: string) => {
    if (status === "archived") {
      return "Make active";
    } else {
      return "Make inactive";
    }
  };

  const invitationStatus = (status: string) => {
    return status === "accepted"
      ? "Accepted"
      : status === "expired"
      ? "Expired"
      : "Invited";
  };

  const setStatus = (status: string) => {
    if (status === "archived") {
      return "approved";
    } else {
      return "archived";
    }
  };

  const updateSortedRows = (
    expertsRows: Expert[],
    requestsRows: RequestRow[],
    filteredInvitationRows: Invitation[],
    originalInvitationRows: Invitation[]
  ) => {
    sortedRows.value.activeExperts = expertsRows.filter((expert) => {
      const status = expertStatusText(
        expert.joinProgramRequest?.[0]?.status || "",
        expert,
        originalInvitationRows
      );
      return status === "Active";
    });

    sortedRows.value.requests = [...requestsRows];

    sortedRows.value.interestedExperts = expertsRows.filter((expert) => {
      const status = expertStatusText(
        expert.joinProgramRequest?.[0]?.status || "",
        expert,
        originalInvitationRows
      );
      return status === "Interested in Applying";
    });

    sortedRows.value.invitedExperts = expertsRows.filter((expert) => {
      const status = expertStatusText(
        expert.joinProgramRequest?.[0]?.status || "",
        expert,
        originalInvitationRows
      );
      return status === "Invited";
    });

    sortedRows.value.interestedSignupExperts = expertsRows.filter((expert) => {
      const status = expertStatusText(
        expert.joinProgramRequest?.[0]?.status || "",
        expert,
        originalInvitationRows
      );
      return status === "Signup In Progress";
    });

    sortedRows.value.invitations = [...filteredInvitationRows];

    sortedRows.value.hiddenExperts = expertsRows.filter((expert) => {
      const status = expertStatusText(
        expert.joinProgramRequest?.[0]?.status || "",
        expert,
        originalInvitationRows
      );
      return status === "Hidden - Calendar Disconnected";
    });

    sortedRows.value.otherExperts = expertsRows.filter((expert) => {
      const status = expertStatusText(
        expert.joinProgramRequest?.[0]?.status || "",
        expert,
        originalInvitationRows
      );
      return ![
        "Active",
        "Interested in Applying",
        "Invited",
        "Signup In Progress",
        "Hidden - Calendar Disconnected",
      ].includes(status);
    });
  };

  // TODO: Move other methods here as well
  // - fetchExperts
  // - fetchRequests
  // - handleSuspendExpert
  // - handleSendInvitation
  // - performAction
  // - etc.

  return {
    sortedRows,
    expertStatusText,
    pendingInvitation,
    status,
    updateSortedRows,
    lastActivityText,
    convertToDate,
    determineActionType,
    performActionText,
    getClassForStatus,
    getStatusTooltip,
    expertButtonText,
    invitationStatus,
    setStatus,
  };
}
